/**
 * B2b：浏览器 → OSS 预签名 PUT 直传，再 complete 净化入库。
 * local / 未启用时由调用方回退 multipart。
 * PUT 部分失败时只回退失败文件，禁止整批改 multipart。
 */
import { UPLOAD_CONFIG } from './uploadConfig';
import {
  mapWithUploadConcurrency,
  UPLOAD_CHUNK_CONCURRENCY,
  UPLOAD_SAVE_CONCURRENCY,
} from './globalUploadBudget';
import { mergeUploadViewUrlMaps } from './uploadBatchViewUrls';
import {
  isAsyncProcessingComplete,
  resolveBatchCompleteToReady,
  resolveCompleteToReady,
} from './uploadSanitizeWait';

/** @type {{ promise: Promise<object>, failedAt: number }|null} */
let capabilitiesCache = null;
const CAPABILITIES_FAIL_TTL_MS = 30_000;

export function resetUploadCapabilitiesCache() {
  capabilitiesCache = null;
}

const FALLBACK_CAPABILITIES = {
  directOss: false,
  asyncSanitize: false,
  maxFilesPerRequest: UPLOAD_CONFIG.chunkSize || 20,
  maxBytes: 0,
};

/** @typedef {'capabilities'|'presign'|'put'|'cors'|'complete'|'aborted'|'unknown'} DirectUploadErrorKind */

export class DirectUploadError extends Error {
  /**
   * @param {string} message
   * @param {{ kind?: DirectUploadErrorKind, status?: number, code?: string, retryAfterSeconds?: number, failedFiles?: File[] }} [meta]
   */
  constructor(message, meta = {}) {
    super(message);
    this.name = 'DirectUploadError';
    this.kind = meta.kind || 'unknown';
    this.status = meta.status;
    this.code = meta.code;
    this.retryAfterSeconds = meta.retryAfterSeconds;
    this.failedFiles = Array.isArray(meta.failedFiles) ? meta.failedFiles : [];
  }
}

function wrapCompleteError(error, failedFiles) {
  if (error?.name === 'AbortError') {
    return new DirectUploadError('上传已取消', { kind: 'aborted' });
  }
  return new DirectUploadError(error?.message || '直传确认失败', {
    kind: 'complete',
    status: error?.status,
    code: error?.code,
    retryAfterSeconds: error?.retryAfterSeconds,
    failedFiles,
  });
}

export function isDirectUploadFatalError(error) {
  if (!error) return false;
  if (error.name === 'AbortError' || error?.kind === 'aborted') return true;
  const kind = error?.kind;
  return kind === 'capabilities' || kind === 'presign' || kind === 'cors';
}

export async function fetchUploadCapabilities(request) {
  const now = Date.now();
  if (capabilitiesCache?.promise) {
    if (!capabilitiesCache.failedAt || now - capabilitiesCache.failedAt < CAPABILITIES_FAIL_TTL_MS) {
      return capabilitiesCache.promise;
    }
  }
  const promise = request('/signed-image/upload-capabilities', {
    method: 'GET',
    tokenType: 'funding',
  })
    .then((caps) => {
      const resolved = {
        directOss: caps?.directOss === true,
        asyncSanitize: caps?.asyncSanitize === true,
        maxFilesPerRequest: Number(caps?.maxFilesPerRequest) || UPLOAD_CONFIG.chunkSize || 20,
        maxBytes: Number(caps?.maxBytes) || 0,
      };
      capabilitiesCache = { promise: Promise.resolve(resolved), failedAt: 0 };
      return resolved;
    })
    .catch((error) => {
      capabilitiesCache = {
        promise: Promise.resolve(FALLBACK_CAPABILITIES),
        failedAt: Date.now(),
      };
      console.warn('[image-storage] multipartFallbackReason=capabilities', error?.message || error);
      return FALLBACK_CAPABILITIES;
    });
  capabilitiesCache = { promise, failedAt: 0 };
  return promise;
}

function resolveContentType(file) {
  const raw = String(file?.type || '').trim().toLowerCase();
  if (raw === 'image/jpg') return 'image/jpeg';
  if (raw === 'image/jpeg' || raw === 'image/png' || raw === 'image/webp') return raw;
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/jpeg';
}

function wrapPutError(error) {
  if (error?.name === 'AbortError') {
    return new DirectUploadError('上传已取消', { kind: 'aborted' });
  }
  const msg = String(error?.message || error || '');
  // 浏览器 CORS 失败常见：Failed to fetch / NetworkError，无 HTTP status
  if (
    error instanceof TypeError
    || /failed to fetch|networkerror|cors|access-control/i.test(msg)
  ) {
    return new DirectUploadError(
      `OSS 直传 CORS/网络失败（请检查桶 CORS 是否允许本站 PUT+Content-Type）：${msg.slice(0, 160)}`,
      { kind: 'cors' },
    );
  }
  return new DirectUploadError(msg || 'OSS PUT 失败', { kind: 'put' });
}

async function putToOss(uploadUrl, file, contentType, signal) {
  let response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType,
      },
      credentials: 'omit',
      mode: 'cors',
      signal,
    });
  } catch (error) {
    throw wrapPutError(error);
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new DirectUploadError(
      `OSS 直传失败(${response.status})${text ? `: ${text.slice(0, 120)}` : ''}`,
      { kind: 'put', status: response.status },
    );
  }
}

function slotToCompleteItem(slot, fileName) {
  return {
    objectKey: slot.objectKey,
    folder: slot.folder,
    contentType: slot.contentType,
    size: slot.size,
    expiresAt: slot.expiresAt,
    uploadToken: slot.uploadToken,
    fileName: fileName || slot.fileName || undefined,
  };
}

function emptyBatchResult(extra = {}) {
  return {
    saved: 0,
    failed: 0,
    urls: [],
    failedFiles: [],
    ...extra,
  };
}

function mergeChunkCompletes(chunkResults) {
  const allUrls = [];
  const allIds = [];
  let saved = 0;
  let failed = 0;
  let lastError = '';
  const failedFiles = [];
  for (const complete of chunkResults) {
    const urls = Array.isArray(complete?.urls) ? complete.urls : [];
    allUrls.push(...urls);
    if (Array.isArray(complete?.ids)) {
      allIds.push(...complete.ids);
    }
    saved += Number(complete?.saved) || urls.length;
    failed += Number(complete?.failed) || 0;
    if (complete?.lastError) {
      lastError = complete.lastError;
    }
    if (Array.isArray(complete?.failedFiles)) {
      failedFiles.push(...complete.failedFiles);
    }
  }

  const mergedViews = mergeUploadViewUrlMaps(chunkResults);
  const result = {
    saved,
    failed,
    urls: allUrls,
    failedFiles,
    viewUrls: mergedViews.viewUrls,
    displayUrls: mergedViews.displayUrls,
  };
  if (allUrls.length > 0) {
    result.url = allUrls[0];
  }
  if (allIds.length > 0) {
    result.ids = allIds;
  }
  if (mergedViews.viewUrl) {
    result.viewUrl = mergedViews.viewUrl;
  }
  if (mergedViews.displayUrl) {
    result.displayUrl = mergedViews.displayUrl;
  }
  if (lastError) {
    result.lastError = lastError;
  }
  return result;
}

/**
 * 单文件：presign → PUT → complete
 * @returns {Promise<object>} normalizeUploadResponseUrl 前的原始结果
 */
export async function directUploadOneFile({
  request,
  file,
  folder,
  source,
  signal,
}) {
  const contentType = resolveContentType(file);
  let slot;
  try {
    slot = await request('/signed-image/presign-upload', {
      method: 'POST',
      data: {
        folder,
        contentType,
        size: file.size,
        fileName: file.name || undefined,
      },
      tokenType: 'funding',
      timeoutMs: UPLOAD_CONFIG.timeoutMs,
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new DirectUploadError('上传已取消', { kind: 'aborted' });
    }
    throw new DirectUploadError(error?.message || '预签名失败', { kind: 'presign' });
  }
  await putToOss(slot.uploadUrl, file, slot.contentType || contentType, signal);
  try {
    const complete = await request('/signed-image/complete-upload', {
      method: 'POST',
      data: {
        ...slotToCompleteItem(slot, file.name),
        source: source || undefined,
      },
      tokenType: 'funding',
      timeoutMs: UPLOAD_CONFIG.timeoutMs,
      signal,
    });
    // Flag 开：202 processing → 底层轮询至 ready，调用方仍拿到 url
    return await resolveCompleteToReady(request, complete, { signal });
  } catch (error) {
    throw wrapCompleteError(error, [file]);
  }
}

/**
 * 单分片：presign 整片 → 按文件 PUT（失败不拖垮整片）→ 仅对 PUT 成功项 complete。
 * @param {{ deferReadyWait?: boolean }} [opts] T-A12：true 时 complete 后不 await sanitize ready（由上层汇聚等待）
 * @returns {Promise<object>} 含 urls / failedFiles
 */
async function uploadOneChunk({
  request,
  chunk,
  folder,
  source,
  signal,
  deferReadyWait = false,
}) {
  let slots;
  try {
    const presign = await request('/signed-image/presign-upload-batch', {
      method: 'POST',
      data: {
        folder,
        files: chunk.map((file) => ({
          contentType: resolveContentType(file),
          size: file.size,
          fileName: file.name || undefined,
        })),
      },
      tokenType: 'funding',
      timeoutMs: UPLOAD_CONFIG.batchTimeoutMs,
      signal,
    });
    slots = Array.isArray(presign?.items) ? presign.items : [];
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new DirectUploadError('上传已取消', { kind: 'aborted' });
    }
    throw new DirectUploadError(error?.message || '批量预签名失败', {
      kind: 'presign',
      failedFiles: chunk,
    });
  }
  if (slots.length !== chunk.length) {
    throw new DirectUploadError('直传凭证数量与文件数不一致', {
      kind: 'presign',
      failedFiles: chunk,
    });
  }

  /** @type {Array<{ index: number, file: File, slot: object }>} */
  const putOk = [];
  /** @type {File[]} */
  const failedFiles = [];
  /** @type {DirectUploadError|null} */
  let firstPutError = null;

  await mapWithUploadConcurrency(chunk, UPLOAD_SAVE_CONCURRENCY, async (file, index) => {
    if (signal?.aborted) {
      failedFiles.push(file);
      return;
    }
    const slot = slots[index];
    try {
      await putToOss(slot.uploadUrl, file, slot.contentType || resolveContentType(file), signal);
      putOk.push({ index, file, slot });
    } catch (error) {
      failedFiles.push(file);
      if (!firstPutError && error instanceof DirectUploadError) {
        firstPutError = error;
      } else if (!firstPutError) {
        firstPutError = wrapPutError(error);
      }
    }
  });

  // 整片 PUT 全失败：若是 CORS，视为致命（整批应提示运维修 CORS，并可整批 multipart）
  if (putOk.length === 0) {
    const kind = firstPutError?.kind === 'cors' ? 'cors' : 'put';
    throw new DirectUploadError(
      firstPutError?.message || 'OSS PUT 全部失败',
      { kind, failedFiles: chunk },
    );
  }

  if (failedFiles.length > 0) {
    console.warn(
      `[image-storage] multipartFallbackReason=put_partial ok=${putOk.length} fail=${failedFiles.length}`,
      firstPutError?.message || '',
    );
  }

  putOk.sort((a, b) => a.index - b.index);
  let complete;
  try {
    complete = await request('/signed-image/complete-upload-batch', {
      method: 'POST',
      data: {
        source: source || undefined,
        items: putOk.map(({ slot, file }) => slotToCompleteItem(slot, file?.name)),
      },
      tokenType: 'funding',
      timeoutMs: UPLOAD_CONFIG.batchTimeoutMs,
      signal,
    });
    // 中途背压：已入队项保留 uploadIds；failed items 对应文件交给上层 multipart/重试
    const enqueueFailedFiles = [];
    if (Array.isArray(complete?.items)) {
      complete.items.forEach((item, i) => {
        const status = String(item?.status || '').toLowerCase();
        if (status === 'failed' && putOk[i]?.file) {
          enqueueFailedFiles.push(putOk[i].file);
        }
      });
    }
    if (!deferReadyWait) {
      complete = await resolveBatchCompleteToReady(request, complete, { signal });
    }
    console.info(
      `[image-storage] directOssSuccess chunk=${chunk.length} completed=${putOk.length}`
        + ` putFailed=${failedFiles.length} enqueueFailed=${enqueueFailedFiles.length}`
        + ` deferReady=${Boolean(deferReadyWait)}`,
    );
    return {
      ...complete,
      failedFiles: [...failedFiles, ...enqueueFailedFiles],
      deferredReady: Boolean(deferReadyWait && isAsyncProcessingComplete(complete)),
    };
  } catch (error) {
    throw wrapCompleteError(error, [...putOk.map((row) => row.file), ...failedFiles]);
  }
}

/**
 * T-A12：将仍 processing 的分片结果汇聚为 ready（与同步路径同形 urls）。
 * @param {Function} request
 * @param {object[]} chunkResults
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function settleDeferredDirectChunkResults(request, chunkResults, options = {}) {
  const list = Array.isArray(chunkResults) ? chunkResults : [];
  const settled = await Promise.all(list.map(async (part) => {
    if (!part || typeof part !== 'object') return part;
    if (!part.deferredReady && !isAsyncProcessingComplete(part)) {
      return part;
    }
    const ready = await resolveBatchCompleteToReady(request, part, options);
    const { deferredReady, ...rest } = ready;
    return { ...rest, deferredReady: false };
  }));
  return settled;
}

/**
 * 多文件：分批 presign → 有界并发 PUT → complete-batch。
 * T-A12：`pipelineOverlap=true` 时各分片 complete 后不阻塞 waitReady，批末汇聚轮询。
 * 返回结果含 failedFiles：仅这些文件应由调用方走 multipart，勿整批回退。
 */
export async function directUploadFilesBatch({
  request,
  files,
  folder,
  source,
  signal,
  chunkSize = UPLOAD_CONFIG.chunkSize || 20,
  chunkConcurrency = UPLOAD_CHUNK_CONCURRENCY,
  pipelineOverlap = UPLOAD_CONFIG.pipelineOverlap === true,
}) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  if (list.length === 0) {
    return emptyBatchResult();
  }

  const size = Math.max(1, chunkSize);
  const offsets = [];
  for (let offset = 0; offset < list.length; offset += size) {
    offsets.push(offset);
  }

  /** @type {Map<number, object>} */
  const resultsByOffset = new Map();
  /** @type {File[]} */
  const deferredMultipart = [];
  /** @type {Promise<void>[]} */
  const readyWaiters = [];
  const overlap = pipelineOverlap === true;

  await mapWithUploadConcurrency(
    offsets,
    Math.max(1, chunkConcurrency),
    async (offset) => {
      if (signal?.aborted) {
        resultsByOffset.set(offset, {
          saved: 0,
          failed: 0,
          urls: [],
          failedFiles: list.slice(offset, offset + size),
          lastError: '已取消',
        });
        return;
      }
      const chunk = list.slice(offset, offset + size);
      try {
        const result = await uploadOneChunk({
          request,
          chunk,
          folder,
          source,
          signal,
          deferReadyWait: overlap,
        });
        if (overlap && (result.deferredReady || isAsyncProcessingComplete(result))) {
          resultsByOffset.set(offset, result);
          // 立刻开始 waitReady，与后续分片 PUT 重叠
          readyWaiters.push(
            resolveBatchCompleteToReady(request, result, { signal }).then((ready) => {
              const { deferredReady, ...rest } = ready;
              resultsByOffset.set(offset, {
                ...rest,
                failedFiles: result.failedFiles || [],
                deferredReady: false,
              });
            }),
          );
        } else {
          resultsByOffset.set(offset, result);
        }
      } catch (error) {
        if (error instanceof DirectUploadError && error.kind === 'aborted') {
          throw error;
        }
        // presign/cors/整片 put 失败：整片交给 multipart；complete 失败同理
        if (
          error instanceof DirectUploadError
          && (error.kind === 'presign' || error.kind === 'cors' || error.kind === 'put' || error.kind === 'complete')
        ) {
          const failed = error.failedFiles?.length ? error.failedFiles : chunk;
          console.warn(
            `[image-storage] multipartFallbackReason=${error.kind} files=${failed.length}`,
            error.message,
          );
          // cors/presign：向上抛，让调用方决定是否整批 multipart（并清 capabilities）
          if (error.kind === 'cors' || error.kind === 'presign') {
            throw error;
          }
          deferredMultipart.push(...failed);
          resultsByOffset.set(offset, {
            saved: 0,
            failed: failed.length,
            urls: [],
            failedFiles: failed,
            lastError: error.message,
          });
          return;
        }
        throw error;
      }
    },
  );

  if (readyWaiters.length > 0) {
    await Promise.all(readyWaiters);
  }

  const chunkResults = offsets
    .map((offset) => resultsByOffset.get(offset))
    .filter(Boolean);

  const merged = mergeChunkCompletes(chunkResults);
  if (deferredMultipart.length > 0) {
    merged.failedFiles = [...(merged.failedFiles || []), ...deferredMultipart];
  }
  if (overlap) {
    merged.pipelineOverlap = true;
  }
  return merged;
}
